<template>
  <div class="gi-root">

    <!-- Summary cards -->
    <!-- Kept on screen while a toggle change refetches, so the cards
         don't blink out; only hidden until the first load lands. -->
    <div class="gi-summary-cards" v-if="summaryLoaded" :class="{ 'gi-summary-cards--loading': loadingSummary }">
      <div class="gi-card gi-period-card">
        <div class="gi-card__title">
          Last Period
          <span v-if="billingType !== 'Both'" class="gi-card__scope">{{ billingType }}</span>
          <span v-if="filteredSummary" class="gi-card__scope gi-card__scope--filtered">Filtered</span>
        </div>
        <table class="gi-period-table">
          <tbody>
            <tr
              v-for="row in lastPeriodRows"
              :key="row.label"
              :class="{ 'gi-period-table__row--amount': row.big }"
            >
              <td class="gi-period-table__label">{{ row.label }}</td>
              <td class="gi-period-table__value">{{ row.value }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="gi-card gi-period-card">
        <div class="gi-card__title">
          Current Period
          <span v-if="billingType !== 'Both'" class="gi-card__scope">{{ billingType }}</span>
          <span v-if="filteredSummary" class="gi-card__scope gi-card__scope--filtered">Filtered</span>
          <span ref="currentPeriodInfoIcon" class="gi-info-icon ml-1"><b-icon icon="info-circle" /></span>
          <b-tooltip :target="() => $refs.currentPeriodInfoIcon" placement="top" triggers="hover focus">
            Approved + Draft
          </b-tooltip>
        </div>
        <table class="gi-period-table">
          <tbody>
            <tr
              v-for="row in currentPeriodRows"
              :key="row.label"
              :class="{ 'gi-period-table__row--amount': row.big }"
            >
              <td class="gi-period-table__label">{{ row.label }}</td>
              <td class="gi-period-table__value">{{ row.value }}</td>
              <td class="gi-period-table__variance">
                <span
                  v-if="row.variance && row.variance.pct !== null && row.variance.pct !== undefined"
                  :class="row.variance.direction === 'up' ? 'gi-variance--up' : 'gi-variance--down'"
                >
                  {{ row.variance.direction === 'up' ? '▲' : '▼' }} {{ Math.abs(row.variance.pct) }}%
                </span>
                <span v-else class="gi-variance--muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <PendingInvoicesTable
      :key="'pending-' + refreshToken"
      :billing-type.sync="billingType"
      @changed="handleChanged"
      @filtered="handleDraftsFiltered"
    />

    <ApprovedDispatchedInvoicesTable
      :key="'approved-' + refreshToken + '-' + (activeFilterMode || 'none')"
      :filter-mode="activeFilterMode"
      @changed="handleChanged"
    />

  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from '@vue/composition-api'

import InvoicingService from '@services/InvoicingService'
import { formatCurrency } from './useInvoiceExport'
import PendingInvoicesTable from './PendingInvoicesTable.vue'
import ApprovedDispatchedInvoicesTable from './ApprovedDispatchedInvoicesTable.vue'
import { hasDraftFilters, summaryForDrafts } from './draftSummary'

export default defineComponent({
  name: 'GenerateInvoicesTab',

  components: {
    PendingInvoicesTable,
    ApprovedDispatchedInvoicesTable,
  },

  props: {
    // 'needs-attention' | 'changed-after-approve' | null — set via the
    // header warning icon / this page's "changed since approval" banner.
    filterMode: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const summary = ref({})
    const loadingSummary = ref(false)
    const summaryLoaded = ref(false)

    // The Draft table's Market / LIMOSS / Both toggle — both summary cards
    // are narrowed to it (see Lincoln's InvoiceSummaryView billing_type).
    const billingType = ref('Both')

    // Bumped on any Pending/Approved table 'changed' event — forces both
    // child tables to remount and refetch rather than optimistically
    // mutating local state, since Lincoln (not LimeIQ) owns invoice truth.
    const refreshToken = ref(0)

    // Route-driven filter mode ('needs-attention' | 'changed-after-approve')
    // passed straight through to the Approved table — set via the header
    // warning icon / this page's own banner.
    const activeFilterMode = ref(props.filterMode)
    watch(() => props.filterMode, (val) => { activeFilterMode.value = val })

    // What the Draft table is currently showing (every page).
    const draftView = ref({ rows: [], filterMap: {}, search: '' })
    const handleDraftsFiltered = (view) => { draftView.value = view }

    // Once the Draft table is searched or column-filtered (invoice no.,
    // TPA, underwriter, …), both cards are worked out from just the rows it
    // matches — see summaryForDrafts. Otherwise they show the server's
    // figures (which already follow the Market / LIMOSS / Both toggle).
    const filteredSummary = computed(() => {
      if (!hasDraftFilters(draftView.value)) return null
      return summaryForDrafts(draftView.value.rows)
    })
    const shownSummary = computed(() => filteredSummary.value || summary.value || {})

    const lastPeriod = computed(() => shownSummary.value.last_period || {})
    const currentPeriod = computed(() => shownSummary.value.current_period || {})
    const variance = computed(() => shownSummary.value.variance || {})

    const lastPeriodRows = computed(() => [
      { label: 'Billed', value: formatCurrency(lastPeriod.value.amount, 'USD'), big: true },
      { label: 'No. of Invoices', value: lastPeriod.value.invoice_count || 0 },
      { label: 'No. of BDX', value: lastPeriod.value.bdx_count || 0 },
    ])

    // "Unbilled" — since this row is the current (not-yet-billed) period's
    // Approved+Draft total.
    const currentPeriodRows = computed(() => [
      {
        label: 'Unbilled', big: true,
        value: formatCurrency(currentPeriod.value.amount, 'USD'),
        variance: variance.value.amount,
      },
      {
        label: 'No. of Invoices',
        value: currentPeriod.value.invoice_count || 0,
        variance: variance.value.invoice_count,
      },
      {
        label: 'No. of BDX',
        value: currentPeriod.value.bdx_count || 0,
        variance: variance.value.bdx_count,
      },
    ])

    const loadSummary = async () => {
      loadingSummary.value = true
      try {
        const requested = billingType.value
        const resp = await InvoicingService.getInvoiceSummary(
          requested === 'Both' ? {} : { billing_type: requested }
        )
        // A quicker toggle may have moved on while this was in flight.
        if (requested !== billingType.value) return
        const inner = resp.data && resp.data.data
        summary.value = inner && inner.data
        summaryLoaded.value = true
      } catch (e) {
        console.error('Failed to load invoice summary:', e)
      } finally {
        loadingSummary.value = false
      }
    }

    watch(billingType, loadSummary)

    const handleChanged = () => {
      // The Draft table remounts with no search/filters — and reports
      // nothing at all if it comes back empty — so drop the old view.
      draftView.value = { rows: [], filterMap: {}, search: '' }
      refreshToken.value += 1
      loadSummary()
    }

    loadSummary()

    return {
      summary,
      loadingSummary,
      summaryLoaded,
      billingType,
      filteredSummary,
      handleDraftsFiltered,
      refreshToken,

      activeFilterMode,

      lastPeriodRows,
      currentPeriodRows,

      handleChanged,
    }
  },
})
</script>

<style scoped>
.gi-root { padding: 0; }

.gi-root >>> .card .card-body {
    padding: 0 1.5rem!important;
}
.gi-root >>> .container {
    padding: 0!important;
    max-width: initial!important;
}
.gi-root >>> .table-responsive {
    max-height: 300px
}

.gi-summary-cards {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 24px;
}
.gi-card {
  background: #fff; border: 1px solid #e9ecef; border-top: 3px solid #1c4a50;
  border-radius: 8px; padding: 16px 18px;
}
.gi-card__title { font-size: 0.95rem; font-weight: 700; color: #32325d; margin-bottom: 10px; }
.gi-card__scope {
  display: inline-block; margin-left: 6px; padding: 1px 8px; border-radius: 10px;
  background: #e8f1f2; color: #1c4a50; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.04em; vertical-align: middle;
}
.gi-summary-cards--loading { opacity: 0.55; transition: opacity 0.15s ease; }
.gi-card__scope--filtered { background: #fff4e0; color: #b26a00; }

.gi-info-icon { cursor: help; opacity: 0.85; font-size: 12px; color: #8898aa; }

.gi-period-table { width: 100%; border-collapse: collapse; }
.gi-period-table td { padding: 6px 0; border: none; font-size: 13px; color: #525f7f; }
.gi-period-table__label { text-align: left; }
.gi-period-table__value { text-align: right; font-weight: 600; color: #32325d; white-space: nowrap; }
.gi-period-table__variance { text-align: right; width: 90px; font-size: 12px; font-weight: 600; padding-left: 10px; }

.gi-period-table__row--amount .gi-period-table__label,
.gi-period-table__row--amount .gi-period-table__value {
  font-size: 1.5rem; font-weight: 800; color: #1c4a50; padding: 6px 0 10px;
}

.gi-variance--up { color: #2dce89; }
.gi-variance--down { color: #f5365c; }
.gi-variance--muted { color: #8898aa; }
</style>
